import React, { useRef } from 'react'
import ReactDOM from 'react-dom'

import { useTransition } from 'hooks/useTransitions'
import { useSwipeEffects } from 'hooks/useSwipeEffects'
import { useApplyBodyClass } from 'hooks/useScrollEffects'

import { DropdownProps } from 'components/elements/ArchidektDropdown/types'

import Menu from 'components/elements/ArchidektDropdown/Menu'

import { clamp } from 'utils/numbers'

import styles from './drawerMenu.module.scss'

type Props = {
  open: boolean

  onClose: (e?: React.MouseEvent) => void
  onItemClicked: (e: React.MouseEvent, optionOnClick?: () => void, noCloseOnClick?: boolean) => void

  maxInitialOpenHeight?: number
} & DropdownProps

const DrawerMenu = (props: Props) => {
  useApplyBodyClass(props.open, 'no-scrolling')

  const scrollableAreaRef = useRef<HTMLDivElement>(null)

  const swipeRef = useSwipeEffects('vertical', {
    checkCanContinueSwipe: (_e, direction) => {
      if (!open) return false
      if (!direction) return true

      return (scrollableAreaRef?.current?.scrollTop || 0) < 1 && direction === 'down'
    },
    onTouchMove: (desiredOffset: number) => {
      if (!swipeRef.current) return

      swipeRef.current.style.transition = 'unset'
      swipeRef.current.style.transform = `translateY(${desiredOffset < 0 ? -1 * desiredOffset : 0}px)`
    },
    onTouchEnd: (offset, position, speed, direction) => {
      if (!swipeRef.current) return

      const swipeThresholdMet = offset < -1 * (document.documentElement.clientHeight / 2) || (speed > 0.5 && offset < 0)

      swipeRef.current.style.transition = ''

      if (!swipeThresholdMet) {
        swipeRef.current.style.transform = ''

        return
      }

      swipeRef.current.style.transform = 'translateY(100%)'
      swipeRef.current.style.animation = 'unset'

      props.onClose()

      setTimeout(() => {
        if (!swipeRef.current) return

        swipeRef.current.style.transform = ''
        swipeRef.current.style.animation = ''
      }, 400)
    },
  })

  const [overlayRef, overlayStatus] = useTransition(props.open, { transitionInOnMount: true, duration: 200 })
  const [drawerRef] = useTransition(props.open, {
    transitionInOnMount: true,
    enteringClassName: styles.entering,
    exitingClassName: styles.exiting,
  })

  if (!props.open && overlayStatus === 'hidden') return null

  const initialOpenHeight = props.maxInitialOpenHeight ? clamp(30, 90, props.maxInitialOpenHeight) : undefined // Only allows max heights between 30dvh and 90dvh
  const dropdownProps = { ...props, children: undefined } // Clear any children passed from the parent

  return ReactDOM.createPortal(
    <div ref={overlayRef} className={styles.overlay} onClick={props.onClose}>
      <div
        ref={ref => {
          drawerRef.current = ref
          swipeRef.current = ref
        }}
        style={{ maxHeight: initialOpenHeight ? `${initialOpenHeight}dvh` : '' }}
        className={styles.container}>
        <Menu
          {...dropdownProps}
          ref={scrollableAreaRef}
          noTopGap
          customOptionsTop={
            <>
              <div className={styles.dragHandle}>
                <span />
              </div>

              {props.customOptionsTop}
            </>
          }
          menuClassName={styles.drawer}
          customOptionsBottom={<div className={styles.bottomGradient} />}
          unFocusClick={props.onItemClicked}
        />
      </div>
    </div>,
    document.body,
  )
}

export default DrawerMenu
